import { stringify } from 'node:querystring';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { btoa } from 'node:buffer';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { AuthLoginService } from 'src/app/page/auth-page/service/auth-login.service';
import { accordionInterface } from 'src/app/util/interfaces/accordion';
import { genericFormInterface } from 'src/app/util/interfaces/genericForm';
import { kbArchiveInterface } from 'src/app/util/interfaces/kbArchive';
import { logInterface } from 'src/app/util/interfaces/log.interface';
import { requestInterface } from 'src/app/util/interfaces/request';
import { solutionAreaInterface } from 'src/app/util/interfaces/requestClassification';
import { environment } from 'src/environments/environment';
import { CryptoService } from 'src/app/services/crypto/crypto.service';

@Injectable({
	providedIn: 'root',
})
export class KnowledgeBasePageService {
	private getKbAccordionUrl = environment.baseApiUrl + 'kb';
	private postKbArchiveUrl = environment.baseApiUrl + 'kb';
	private putKbArchiveUrl = environment.baseApiUrl + 'kb/{idKb}';
	private getKbArchiveUrl = environment.baseApiUrl + 'kb/{idKb}';
	private deleteKbArchiveUrl = environment.baseApiUrl + 'kb/{idKb}';
	private getKbLogUrl = environment.baseApiUrl + 'kb/{idKb}/log';
	private postKbLogUrl = environment.baseApiUrl + 'kb/{idKb}/log';
	private getMySolutionAreaUrl = environment.baseApiUrl + 'enduser/me/solution_area';
	private searchUrl: string = environment.baseApiUrl + 'tool/search/{search}';
	private getAllRequestURL = environment.baseApiUrl + 'kb/searchRequest';

	private getKbsWithFiltersURL = environment.baseApiUrl + 'kb/searchKb';
	private getAvailableCustomerUrl = environment.baseApiUrl + 'register/form/analyst/availableCustomer';
	private getResponsibleUrl = environment.baseApiUrl + 'request/users';
	private getAnalystUrl = environment.baseApiUrl + 'analyst/all';

	availableStatus = ['New', 'Draft', 'Review', 'Publish', 'Published'];

	headers = new HttpHeaders({
		Authorization: 'Bearer ' + this.AuthLoginService.getSessionToken(),
		'Content-Type': 'application/json;charset=UTF-8',
	});

	noBearerHeaders = new HttpHeaders({ 'Content-Type': 'application/json;charset=UTF-8' });

	constructor(
		private AuthLoginService: AuthLoginService,
		private http: HttpClient,
		private CryptoService: CryptoService
	) {}

	getAvailableCustomer(solutionArea?: string): Observable<genericFormInterface[]> {
		return this.http.get<genericFormInterface[]>(this.getAvailableCustomerUrl, {
			headers: this.headers,
			params: { solutionArea: solutionArea ? solutionArea : '' },
		});
	}

	getKbAccordion(): Observable<accordionInterface[]> {
		return this.http.get(this.getKbAccordionUrl, { headers: this.headers }).pipe(
			map((accordions: any) => {
				const newKbAccordionArray: accordionInterface[] = [];
				for (const accordion of accordions) {
					const newKbAccordion: accordionInterface = {
						id: accordion.name,
						name: accordion.name,
						kb: [],
						type: 'kb',
						dataType: 'kb',
						border: '',
						alert: false,
						accordionLength: 0,
						hidden: false,
					};

					for (const archive of accordion.archive) {
						const newKb: kbArchiveInterface = {
							id: archive.id,
							summary: archive.summary.toString().replaceAll(/(<([^>]+)>)/gi, ''),
							classification: [
								{
									id_solution_area: null,
									solution_area: archive.solution_area,
									request: null,
									classification: null,
									cmdb: null,
									idCustomer: null,
									customer: null,
								},
							],
							language: archive.language,
						};

						newKbAccordion.kb.push(newKb);
					}

					newKbAccordion.accordionLength = newKbAccordion.kb.length;

					newKbAccordionArray.push(newKbAccordion);
				}

				return newKbAccordionArray;
			})
		);
	}

	getKbArchive(idKb: string): Observable<kbArchiveInterface> {
		const getKbArchiveUrl = this.getKbArchiveUrl.replace('{idKb}', idKb);
		return this.http.get<JSON[]>(getKbArchiveUrl, { headers: this.headers }).pipe(
			map((kbRes: any) => {
				kbRes = kbRes[0];

				const newKb: kbArchiveInterface = {
					id: kbRes.id,
					registered_by: kbRes.registered_by,
					driver: kbRes.driver,
					problem_manager: kbRes.problem_manager,
					version: kbRes.version,
					language: kbRes.language,
					version_date: kbRes.version_date,
					register_date: kbRes.register_date,
					summary: kbRes.summary,
					description: kbRes.description,
					points: kbRes.points ? Number.parseInt(kbRes.points) : 0,
					points_vote_qtd: kbRes.points_vote_qtd ? Number.parseInt(kbRes.points_vote_qtd) : 0,
					related_kb: [],
					visibility: kbRes.visibility,
					status: typeof kbRes.status === 'number' ? this.availableStatus[kbRes.status] : kbRes.status,
					active: kbRes.active,
					approved: kbRes.approved,
					to_publisher: kbRes.to_publisher,
					tags: kbRes.tags,
					classification: kbRes.classification,
					responsible: {
						id: kbRes.responsible.id,
						fullName: kbRes.responsible.fullName,
					},
				};

				// if(newKb.request != "") { newKb.relationType = 'request' }
				// else if(newKb.solution_area != "" && newKb.topic != "") { newKb.relationType = 'classification' }

				return newKb;
			})
		);
	}

	getKbLogs(idKb: string): Observable<logInterface[]> {
		const getKbLogUrl = this.getKbLogUrl.replace('{idKb}', idKb);
		return this.http.get(getKbLogUrl, { headers: this.headers }).pipe(
			map((logRes: any) => {
				const newLogArray: logInterface[] = [];

				for (const log of logRes) {
					const newLog: logInterface = {
						idLog: log.id,
						notes: log.notes,
						log_date: log.log_date,
						status: log.status,
						analyst: log.analyst,
						duration: '',
					};

					newLogArray.push(newLog);
				}

				return newLogArray;
			})
		);
	}

	getMySolutionArea(idCustomer?: string): Observable<solutionAreaInterface[]> {
		const getMySolutionAreaUrl = this.getMySolutionAreaUrl + `${idCustomer ? '?idcust=' + idCustomer : ''}`;
		return this.http.get(getMySolutionAreaUrl, { headers: this.headers }).pipe(
			map((solutionAreas: any) => {
				const newSolutionAreaArray: solutionAreaInterface[] = [];

				for (const solutionArea of solutionAreas) {
					const newSolutionArea: solutionAreaInterface = {
						id: solutionArea.id,
						name: solutionArea.group_name,
						label: solutionArea.group_name,
					};

					newSolutionAreaArray.push(newSolutionArea);
				}

				return newSolutionAreaArray;
			})
		);
	}

	postKbArchive(kb: kbArchiveInterface): Observable<any> {
		kb = { ...kb };
		// if(parseInt(kb.solution_area)) kb.id_solution_area = kb.solution_area;
		// kb.search = `${kb.summary};${kb.description};${kb.cause};${kb.resolution};${kb.workaround};${kb.applies}`;
		// kb.search = kb.search.toString().replace( /(<([^>]+)>)/ig, '');

		switch (kb.status) {
			case 'New':
			case 'Draft': {
				kb.status = 0;
				break;
			}

			case 'Review': {
				kb.status = 1;
				break;
			}

			case 'Publish': {
				kb.status = 2;
				break;
			}

			case 'Published': {
				kb.status = 3;
				break;
			}

			default: {
				break;
			}
		}

		return this.http.post(this.postKbArchiveUrl, kb, { headers: this.headers });
	}

	putKbArchive(kb: kbArchiveInterface, nextStatus?: string): Observable<any> {
		const putKbArchiveUrl = this.putKbArchiveUrl.replace('{idKb}', kb.id);
		kb = { ...kb };
		// kb.search = `${kb.summary};${kb.description};${kb.cause};${kb.resolution};${kb.workaround};${kb.applies}`;
		// kb.search = kb.search.toString().replace( /(<([^>]+)>)/ig, '');

		if (nextStatus) kb.status = nextStatus;

		switch (kb.status) {
			case 'New':
			case 'Draft': {
				kb.status = 0;
				break;
			}

			case 'Driver approval': {
				kb.status = 1;
				break;
			}

			case 'Tecnical review': {
				kb.status = 2;
				break;
			}

			case 'Published': {
				kb.status = 3;
				break;
			}

			default: {
				break;
			}
		}

		return this.http.put(putKbArchiveUrl, kb, { headers: this.headers });
	}

	deleteKbArchive(kb: kbArchiveInterface): Observable<any> {
		const deleteKbArchiveUrl = this.deleteKbArchiveUrl.replace('{idKb}', kb.id);
		return this.http.delete(deleteKbArchiveUrl, { headers: this.headers });
	}

	postKbLog(kb: kbArchiveInterface, log: logInterface): Observable<any> {
		const postKbLogUrl = this.postKbLogUrl.replace('{idKb}', kb.id);
		return this.http.post(postKbLogUrl, log, { headers: this.headers });
	}

	decodeSpecialEntitiesHtml(htmlentities: string): string {
		const string_ = document.createElement('textarea');
		string_.innerHTML = htmlentities;
		string_.value = string_.value.replaceAll(String.raw`\n`, '<br />');
		return string_.value;
	}

	searchKb(searchText: string): Observable<kbArchiveInterface[]> {
		const searchUrl = this.searchUrl.replace('{search}', searchText);
		return this.http.get(searchUrl, { headers: this.headers }).pipe(
			map((searchRes: any) => {
				const newKbArray: kbArchiveInterface[] = [];
				for (const kb of searchRes.kb) {
					const newKb: kbArchiveInterface = {
						id: kb.id,
						summary: kb.summary,
					};

					newKbArray.push(newKb);
				}

				return newKbArray;
			})
		);
	}

	bearerlessSearchKb(searchText: string): Observable<kbArchiveInterface[]> {
		const searchUrl = this.searchUrl.replace('{search}', searchText);
		return this.http.get(searchUrl, { headers: this.noBearerHeaders }).pipe(
			map((searchRes: any) => {
				const newKbArray: kbArchiveInterface[] = [];
				for (const kb of searchRes.kb) {
					const newKb: kbArchiveInterface = {
						id: kb.id,
						summary: kb.summary,
					};

					newKbArray.push(newKb);
				}

				return newKbArray;
			})
		);
	}

	getRequestSearchForm(): Observable<genericFormInterface[]> {
		const form: genericFormInterface[] = [
			{
				name: 'requestSearch',
				label: 'lbl_geral_pesquisar',
				inputType: 'string',
				values: [''],
			},
		];

		return of(form);
	}

	getRequest(searchForm: genericFormInterface[], customer: string[], idSolutionArea: string): Observable<requestInterface[]> {
		let getAllRequestsURL = this.getAllRequestURL;
		let queryParameters = searchForm.map((formItem) => `${formItem.name}=${formItem.values[0]}`).join('&');

		const customerParameter = searchForm.find((formItem) => formItem.name === 'customer');
		if (!customerParameter && customer) {
			queryParameters += (queryParameters.length > 0 ? '&' : '') + `customer=${customer}`;
		}

		if (queryParameters.length > 0) {
			getAllRequestsURL += '?' + queryParameters;
		}

		getAllRequestsURL += `&idSolutionArea=${idSolutionArea}`;

		return this.http.get(getAllRequestsURL, { headers: this.headers }).pipe(
			map((requestsRes: any) => {
				const newRequestArray: requestInterface[] = [];
				for (const request of requestsRes) {
					const newRequest: requestInterface = {
						id: request.id,
						summary: request.summary,
						solution_area: request.solution_area,
						id_solution_area: request.id_solution_area,
					};

					newRequestArray.push(newRequest);
				}

				return newRequestArray;
			})
		);
	}

	getRequestData(id?: string, customer?: string[], summary?: string): Observable<any> {
		let parameters = new HttpParams();

		if (id) {
			parameters = parameters.set('id', id);
		}
		if (summary) {
			parameters = parameters.set('summary', summary);
		}
		if (customer && customer.length > 0) {
			for (const c of customer) {
				parameters = parameters.append('customer', c);
			}
		}

		const getRequestDataUrl = `${this.getAllRequestURL}`;
		return this.http.get(getRequestDataUrl, { headers: this.headers, params: parameters });
	}

	getKbsWithFilters(kbInterface: any): Observable<any> {
		let parametros = new HttpParams();

		// Verifica se kbInterface é definido e não está vazio
		if (kbInterface) {
			// Adiciona os parâmetros apenas se eles estiverem definidos
			if (kbInterface.classification && kbInterface.classification.length > 0) {
				parametros = parametros.set('classification', JSON.stringify(kbInterface.classification));
			}
			if (kbInterface.customer && kbInterface.customer.length > 0) {
				parametros = parametros.set('customer', JSON.stringify(kbInterface.customer));
			}
			if (kbInterface.tags && kbInterface.tags.length > 0) {
				parametros = parametros.set('tags', JSON.stringify(kbInterface.tags));
			}
			if (kbInterface.language) {
				parametros = parametros.append('language', `${kbInterface.language}`);
			}
			if (kbInterface.visibility) {
				parametros = parametros.append('visibility', `${kbInterface.visibility}`);
			}
			if (kbInterface.responsible) {
				parametros = parametros.append('responsible', `${kbInterface.responsible}`);
			}
		}
		// Criação dos headers
		const headers = new HttpHeaders({
			Authorization: 'Bearer ' + this.AuthLoginService.getSessionToken(),
			'Content-Type': 'application/json;charset=UTF-8',
		});
		// Retorna a chamada HTTP
		return this.http.get<any>(this.getKbsWithFiltersURL, { params: parametros, headers: headers });
	}

	getAnalysts(): Observable<any[]> {
		return this.http.get<JSON[]>(this.getAnalystUrl, { headers: this.headers }).pipe(
			map((analystsRes: any) => {
				const newAnalystrArray: any[] = [];
				analystsRes.analysts.forEach((analyst: any) => {
					const newAnalyst: any = {
						analyst: {
							id: analyst.id,
							fullName: analyst.fullName,
						},
					};

					newAnalystrArray.push(newAnalyst);
				});
				return newAnalystrArray;
			})
		);
	}

	getSolutionAreaFromAnalyst(): Observable<solutionAreaInterface[]> {
		return this.http.get<any[]>(this.getMySolutionAreaUrl, { headers: this.headers });
	}
}
